import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Plus, Eye, FileText, Search, CheckCircle, XCircle, Edit } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';

const SalesInvoicesListEnhanced = () => {
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchInvoices();
    }
  }, [activeBusiness, statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter.toUpperCase());
      }

      const response = await api.get(`/sales/invoices?${params}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.party_name_snapshot?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-800',
      FINAL: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
      PAID: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50/30 via-white to-teal-50/20 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Sales Invoices</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your sales transactions and GST invoices</p>
        </div>

        {/* Actions Bar */}
        <div className="premium-card p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice number or customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Filters and Actions */}
            <div className="flex items-center gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="final">Final</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <Link to={`/business/${businessId}/sales/invoices/new`}>
                <button className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium">
                  <Plus className="w-4 h-4" />
                  Create Invoice
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="premium-table-container">
          <div className="premium-table-wrapper">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-gray-200 border-t-teal-600 rounded-full animate-spin"></div>
              </div>
            ) : filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  {searchTerm ? 'No invoices found matching your search.' : 'No sales invoices yet.'}
                </p>
                {!searchTerm && (
                  <Link to={`/business/${businessId}/sales/invoices/new`}>
                    <button className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm">
                      Create Your First Invoice
                    </button>
                  </Link>
                )}
              </div>
            ) : (
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>Invoice Number</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Amount</th>
                    <th>Due Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <div className="font-medium text-gray-900 text-sm">{invoice.invoice_number}</div>
                      </td>
                      <td className="text-gray-600 text-sm">
                        {new Date(invoice.date).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="font-medium text-gray-900 text-sm">{invoice.party_name_snapshot}</div>
                        {invoice.party_phone && (
                          <div className="text-xs text-gray-500">{invoice.party_phone}</div>
                        )}
                      </td>
                      <td className="text-gray-600 text-sm">{invoice.items?.length || 0} items</td>
                      <td className="font-medium text-gray-900">₹{invoice.grand_total?.toFixed(2) || '0.00'}</td>
                      <td className="font-medium text-gray-900">
                        ₹{(invoice.grand_total - (invoice.amount_paid || 0))?.toFixed(2) || '0.00'}
                      </td>
                      <td>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          {invoice.status === 'DRAFT' && (
                            <Link to={`/business/${businessId}/sales/invoices/${invoice.id}/edit`}>
                              <button
                                className="premium-icon-button"
                                title="Edit"
                              >
                                <Edit className="premium-icon-sm" />
                              </button>
                            </Link>
                          )}
                          <Link to={`/business/${businessId}/sales/invoices/${invoice.id}`}>
                            <button
                              className="premium-icon-button"
                              title="View Details"
                            >
                              <Eye className="premium-icon-sm" />
                            </button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesInvoicesListEnhanced;
